import { useEffect, useMemo, useState } from "react";
import { routes } from "@/router";
import { ErrorResponseDto } from "@advanced-sales-technologies/backend-client/models/ErrorResponseDto";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { HiOutlineOfficeBuilding } from "react-icons/hi";
import { HiMiniEye, HiOutlineArrowSmallRight, HiOutlineCheckCircle } from "react-icons/hi2";
import { generatePath, useNavigate } from "react-router-dom";

import { useCreateEnrichedList } from "@/hooks/api/list/enrich/useCreateEnrichedList";
import { usePreviewMapping } from "@/hooks/api/list/enrich/usePreviewMapping";
import { EnrichData } from "@/hooks/useCsvUploader";
import { useToast } from "@/hooks/useToast";
import Badge from "@/components/atoms/Badge";
import Button from "@/components/atoms/Button";
import InputError from "@/components/atoms/input/InputError";
import EmptyModalContent from "@/components/atoms/modal-panels/EmptyModalContent";
import CompanyLogo from "@/components/molecules/company/CompanyLogo";
import FormInput from "@/components/molecules/form/FormInput";
import ColumnMapSelectItem, { BASE_OPTIONS } from "@/components/organisms/enrich/ColumnMapSelectItem";
import { searchColumns } from "@/components/organisms/search/searchColumns";

export type MapColumnsModalProps = {
  onClose: (value: boolean) => void;
  onSuccess: () => void;
  preventClose?: boolean;
  listName: string;
  columns: string[];
  guessedColumnsMapping: Record<string, string>;
  mapColumns: (mappings: Record<string, string>[], isPreMapped?: boolean) => EnrichData | undefined;
  mappedData: EnrichData;
  validRowCount: number;
};

type FormValues = {
  listName: string;
  column: Record<string, string>;
};

const previewCount = 10;

const MapColumnsModal = ({
  onClose,
  preventClose = false,
  onSuccess,
  listName,
  columns,
  guessedColumnsMapping,
  mapColumns,
  mappedData,
  validRowCount,
}: MapColumnsModalProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const { previewMapping, data: previewData, isPending: previewIsLoading } = usePreviewMapping();
  const { createEnrichedList, isPending: createEnrichedListIsLoading } = useCreateEnrichedList();
  const watchedFields = useMemo(() => columns.map((column) => `column.${column}` as `column.${string}`), [columns]);
  const defaultValues: FormValues = useMemo(
    () => ({
      listName: listName,
      column: guessedColumnsMapping,
    }),
    [listName, guessedColumnsMapping],
  );
  const methods = useForm<FormValues>({ defaultValues: defaultValues });
  const { setError, handleSubmit, formState, watch, control, setValue, clearErrors } = methods;

  const renderEmptyPreviewRows = (count: number) => {
    return Array.from({ length: count }, () => ({ name: "" }));
  };

  const initPreviewData: { name: string }[] = useMemo(() => renderEmptyPreviewRows(previewCount), []);

  const filledPreviewData: { name: string; logo?: string }[] = useMemo(() => {
    if (!previewData) {
      return initPreviewData;
    }

    const dataLength = previewData.length;
    if (dataLength >= previewCount) {
      return previewData.slice(0, previewCount);
    }

    const emptyRowsCount = previewCount - dataLength;
    const emptyRows = renderEmptyPreviewRows(emptyRowsCount);

    return [...previewData, ...emptyRows];
  }, [previewData, initPreviewData]);

  const fetchPreviews = (items: EnrichData) => {
    if (items && items?.length > 0) {
      // Remove rows with no data and limit to previewCount
      const filteredItems = items.filter((row) => Object.keys(row).length > 0).slice(0, previewCount);
      if (filteredItems.length > 0) {
        previewMapping({ items: filteredItems });
      }
    }
  };

  const onSubmit = (data: FormValues) => {
    if (Object.values(data.column).filter(Boolean).length === 0) {
      setError("column", {
        type: "manual",
        message: t("enrich.map.error"),
      });
      return;
    }

    createEnrichedList(
      { name: data.listName, items: mappedData.filter((obj) => Object.values(obj)[0]) },
      {
        onSuccess: (data) => {
          onSuccess();
          navigate(
            generatePath(routes.listsDetail, {
              id: data.id,
            }),
          );
          showToast({
            featuredIcon: { icon: HiOutlineCheckCircle, color: "success" },
            heading: t("static.addedSuccessfully"),
            children: (
              <Button
                label={t("static.viewLists")}
                onClick={() =>
                  navigate(
                    generatePath(routes.listsDetail, {
                      id: data.id,
                    }),
                  )
                }
                color={"brand"}
                trailingIcon={<HiOutlineArrowSmallRight className={"size-5"} />}
                variants={"text"}
                size={"xs"}
              />
            ),
          });
        },
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        onError: ({ body }: ErrorResponseDto) => {
          if (body?.status_code === 409) {
            setError("listName", {
              type: "manual",
              message: t("static.modalCreate.duplicateError", { name: data.listName }),
            });
          }
        },
      },
    );
  };

  // Set initial values, selected values and fetch previews
  useEffect(() => {
    columns.forEach((column) => {
      setValue(`column.${column}`, guessedColumnsMapping?.[column] || "");
    });
    setSelectedValues(Object.values(guessedColumnsMapping).filter(Boolean));

    const mappings = Object.entries(guessedColumnsMapping).map(([key, value]) => ({ [key]: value }));
    const mappedData = mapColumns(mappings, true);
    if (mappedData) {
      fetchPreviews(mappedData);
    }
  }, [guessedColumnsMapping, columns, setValue]);

  // Subscribe to changes in the form fields without re-rendering the component
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name && name.startsWith("column.")) {
        const selectedValues = columns
          .map((columnKey) => {
            return value.column?.[columnKey];
          })
          .filter(Boolean) as string[];

        setSelectedValues(selectedValues);

        const mappings = columns
          .map((columnKey) => {
            const columnValue = value.column?.[columnKey];
            return { [columnKey]: columnValue || "" };
          })
          .filter((obj) => Object.values(obj)[0]);

        const mappedData = mapColumns(mappings);
        if (mappedData) {
          fetchPreviews(mappedData);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, watchedFields]);

  return (
    <EmptyModalContent preventClose={preventClose} onClose={onClose} padding={"sm"}>
      <FormProvider {...methods}>
        <form
          onSubmit={(e) => {
            clearErrors();
            handleSubmit(onSubmit)(e);
          }}
          className={"grid grid-cols-3"}
        >
          <div className={"col-span-2 flex flex-col gap-8 rounded-bl-2xl rounded-tl-2xl bg-primary p-10"}>
            <div>
              <h2 className={"mb-1 text-xl font-semibold text-primary"}>{t("enrich.heading")}</h2>
              <p className={"max-w-[80%] text-sm text-quaternary"}>{t("enrich.description")}</p>
            </div>

            <FormInput
              name={"listName"}
              className={"w-full max-w-80"}
              label={t("enrich.map.list")}
              placeholder={t("static.modalCreate.placeholder")}
              rules={{ required: t("validation.required"), setValueAs: (value: string) => value.trim() }}
            />

            <div>
              <div className={"rounded-lg bg-secondary"}>
                <div className={"grid grid-cols-2 px-4 py-3 text-sm font-medium"}>
                  <h3>{t("enrich.map.yours")}</h3>
                  <h3>{t("enrich.map.attribute")}</h3>
                </div>
              </div>

              <div className={"max-h-64 min-h-10 overflow-y-auto"}>
                {columns.map((column, index) => (
                  <Controller
                    key={index}
                    name={`column.${column}`}
                    control={control}
                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                      <ColumnMapSelectItem
                        column={column}
                        selectedValue={value}
                        onChange={onChange}
                        options={BASE_OPTIONS(t)}
                        disabledOptions={selectedValues}
                        hasError={!!error}
                      />
                    )}
                  />
                ))}
              </div>
              <div className={"w-1/2"}>
                {selectedValues.length > 0 &&
                  (validRowCount === 0 ? (
                    <InputError error={t("enrich.map.validation.error")} showIcon />
                  ) : (
                    <p className="text-sm text-tertiary">
                      <Trans
                        i18nKey="enrich.map.validation.success"
                        values={{ validRows: validRowCount, rows: mappedData.length }}
                        components={{ strong: <strong /> }}
                      />
                    </p>
                  ))}

                {selectedValues.length === 0 && formState.errors.column && (
                  <InputError error={formState.errors.column.message! as unknown as string} showIcon />
                )}
              </div>
            </div>

            <div className="mx-auto mt-4 grid w-full min-w-96 max-w-[70%] grid-cols-2 gap-3">
              <Button
                type={"button"}
                label={t("cancel")}
                onClick={() => onClose(false)}
                color="natural"
                variants="outline"
              />
              <Button
                type={"submit"}
                label={t("enrich.map.create")}
                color="brand"
                variants="primary"
                isLoading={createEnrichedListIsLoading || formState.isSubmitting}
              />
            </div>
          </div>
          <div className={"h-full space-y-6 overflow-hidden rounded-br-2xl rounded-tr-2xl bg-secondary p-10"}>
            <Badge
              label={t("enrich.map.preview")}
              color={"white"}
              leadingIcon={<HiMiniEye className={"text-fg-quinary"} />}
            />

            <div className={"relative overflow-x-auto rounded-lg border border-secondary"}>
              <div
                className={
                  "flex select-none items-center gap-1.5 truncate rounded-tl-lg rounded-tr-lg border-b border-b-primary bg-secondary px-4 py-2.5 text-sm font-medium text-tertiary"
                }
              >
                <HiOutlineOfficeBuilding className={"size-4 stroke-2 text-quaternary"} />
                {searchColumns(t).company_name.name}
              </div>

              {/* Preview Table */}
              <div className={"divide-y divide-secondary"}>
                {filledPreviewData?.map((row, index) => {
                  if (previewIsLoading) {
                    return <div key={index} className={"h-9 w-full animate-pulse bg-gray-100"} />;
                  }

                  const { name, logo } = row;
                  return (
                    <div
                      key={index}
                      className={
                        "flex h-9 select-none items-center truncate bg-white px-4 py-1.5 text-left text-sm hover:bg-gray-50"
                      }
                    >
                      {!name ? (
                        <div className={"text-quaternary"}>{t("enrich.map.nothingFound")}</div>
                      ) : (
                        <div key={index} className={"flex items-center space-x-2 font-medium text-primary"}>
                          <CompanyLogo url={logo || null} />
                          <span>{name}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </form>
      </FormProvider>
    </EmptyModalContent>
  );
};

export default MapColumnsModal;
